/**
 * NotificationRow — single-notification row, shared by the bell dropdown
 * (DashboardLayout.js) and NotificationHistoryModal so both render the
 * exact same markup instead of two hand-maintained copies.
 *
 * Props:
 *   notification  — { id, title, message, created_at, is_read, action_url,
 *                      notification_type }
 *   onClick       — (notification) => void
 *   onToggleRead  — optional (notification) => void; when passed, shows a
 *                   hover-visible mark read/unread button (history modal only)
 *   onDelete      — optional (notification) => void; when passed, shows a
 *                   hover-visible delete button (history modal only)
 */
import React from 'react';
import {
  AlertCircle, AlertTriangle, CheckCircle2, Info, Mail, MailOpen, Trash2, XCircle,
} from 'lucide-react';
import { formatDistanceToNowStrict } from 'date-fns';

// notification_type -> icon/color, matching Notification.notification_type's
// choices (apps/core/models.py) -- lets a long history list be scanned by
// eye instead of reading every title.
const TYPE_ICONS = {
  info: { Icon: Info, className: 'text-blue-500' },
  warning: { Icon: AlertTriangle, className: 'text-amber-500' },
  error: { Icon: XCircle, className: 'text-rose-500' },
  success: { Icon: CheckCircle2, className: 'text-emerald-500' },
  action_required: { Icon: AlertCircle, className: 'text-purple-500' },
};

export default function NotificationRow({ notification, onClick, onToggleRead, onDelete }) {
  const { Icon, className } = TYPE_ICONS[notification.notification_type] || TYPE_ICONS.info;
  const hasRowActions = !!(onToggleRead || onDelete);
  const created = notification.created_at ? new Date(notification.created_at) : null;

  return (
    <div
      className="group p-3 border-b border-slate-50 hover:bg-slate-50 cursor-pointer"
      onClick={() => onClick?.(notification)}
      data-testid={`notification-item-${notification.id}`}
    >
      <div className="flex items-start gap-2">
        <Icon className={`h-4 w-4 mt-0.5 shrink-0 ${className}`} aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <p className="text-sm font-medium text-slate-800">{notification.title}</p>
            {!notification.is_read && (
              <span className="w-1.5 h-1.5 rounded-full bg-blue-500 shrink-0" aria-hidden="true" title="Unread" />
            )}
          </div>
          <p className="text-xs text-slate-500 mt-0.5">{notification.message}</p>
          <p className="text-xs text-slate-400 mt-1" title={created ? created.toLocaleString() : ''}>
            {created ? formatDistanceToNowStrict(created, { addSuffix: true }) : 'Just now'}
          </p>
        </div>
        {hasRowActions && (
          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
            {onToggleRead && (
              <button
                type="button"
                aria-label={notification.is_read ? 'Mark unread' : 'Mark read'}
                title={notification.is_read ? 'Mark unread' : 'Mark read'}
                data-testid={`notification-item-${notification.id}-toggle-read`}
                className="p-1 rounded hover:bg-slate-200 text-slate-500"
                onClick={(e) => { e.stopPropagation(); onToggleRead(notification); }}
              >
                {notification.is_read ? <Mail className="h-3.5 w-3.5" /> : <MailOpen className="h-3.5 w-3.5" />}
              </button>
            )}
            {onDelete && (
              <button
                type="button"
                aria-label="Delete notification"
                title="Delete"
                data-testid={`notification-item-${notification.id}-delete`}
                className="p-1 rounded hover:bg-rose-100 text-slate-500 hover:text-rose-600"
                onClick={(e) => { e.stopPropagation(); onDelete(notification); }}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
